import DOMPurify from "dompurify";
import { marked } from "marked";
import { truncateText } from "./format.ts";
import {
  encodeCopyPayload,
  highlightFenceWithLineNumbers,
  highlightInline,
  preprocessMarkdown,
} from "./markdown-highlight.ts";

marked.setOptions({
  gfm: true,
  breaks: true,
});

marked.use({
  renderer: {
    code({ text, lang }: { text: string; lang?: string }) {
      const label = lang?.trim() || "text";
      const highlighted = highlightFenceWithLineNumbers(lang, text);
      const encoded = encodeCopyPayload(text);
      // Long fenced blocks (diffs, logs, large files) render inside a
      // <details> so the transcript stays scannable. Threshold: 20+ lines
      // OR >2KB of code. The first few lines are shown by default so the
      // operator gets a hint of what's inside.
      const isLong = text.length > 2048 || text.split("\n").length > 20;
      const inner = `<pre class="chat-code-block"><code class="hljs language-${escapeHtml(label)}">${highlighted}</code></pre>`;
      if (!isLong) {
        return `<div class="chat-code-block-wrap" data-raw="${encoded}"><div class="chat-code-block-wrap__header"><span class="chat-code-block-wrap__lang">${escapeHtml(label)}</span><button type="button" class="chat-code-block-wrap__copy btn subtle btn--sm">Copy</button></div>${inner}</div>`;
      }
      const lineCount = text.split("\n").length;
      return `<div class="chat-code-block-wrap chat-code-block-wrap--long" data-raw="${encoded}"><div class="chat-code-block-wrap__header"><span class="chat-code-block-wrap__lang">${escapeHtml(label)}</span><span class="chat-code-block-wrap__meta">${lineCount} lines</span><button type="button" class="chat-code-block-wrap__copy btn subtle btn--sm">Copy</button></div><details class="chat-code-block-wrap__details"><summary class="chat-code-block-wrap__summary"><span class="chat-code-block-wrap__chevron" aria-hidden="true">▸</span><span class="chat-code-block-wrap__summary-text">Show full block</span></summary>${inner}</details></div>`;
    },
    // UX-R12 / JAISIU-3336: also highlight inline (backtick-wrapped) code so
    // short snippets pick up syntax colors. Falls back to escaped plain text
    // when hljs cannot classify the snippet, so we never drop content.
    codespan({ text }: { text: string }) {
      const highlighted = highlightInline(text);
      return `<code class="hljs">${highlighted}</code>`;
    },
  },
});

const allowedTags = [
  "a",
  "b",
  "blockquote",
  "br",
  "button",
  "code",
  "del",
  "details",
  "div",
  "em",
  "h1",
  "h2",
  "h3",
  "h4",
  "hr",
  "i",
  "li",
  "ol",
  "p",
  "pre",
  "span",
  "strong",
  "summary",
  "table",
  "tbody",
  "td",
  "th",
  "thead",
  "tr",
  "ul",
];

const allowedAttrs = [
  "class",
  "data-raw",
  "data-user-opened",
  "href",
  "open",
  "rel",
  "target",
  "title",
  "start",
  "type",
  "aria-label",
];

let hooksInstalled = false;
const MARKDOWN_CHAR_LIMIT = 140_000;
const MARKDOWN_PARSE_LIMIT = 40_000;
const MARKDOWN_CACHE_LIMIT = 200;
const MARKDOWN_CACHE_MAX_CHARS = 50_000;
const markdownCache = new Map<string, string>();

function getCachedMarkdown(key: string): string | null {
  const cached = markdownCache.get(key);
  if (cached === undefined) {
    return null;
  }
  markdownCache.delete(key);
  markdownCache.set(key, cached);
  return cached;
}

function setCachedMarkdown(key: string, value: string) {
  markdownCache.set(key, value);
  if (markdownCache.size <= MARKDOWN_CACHE_LIMIT) {
    return;
  }
  const oldest = markdownCache.keys().next().value;
  if (oldest) {
    markdownCache.delete(oldest);
  }
}

function installHooks() {
  if (hooksInstalled) {
    return;
  }
  hooksInstalled = true;

  DOMPurify.addHook("afterSanitizeAttributes", (node: Element) => {
    if (!(node instanceof HTMLAnchorElement)) {
      return;
    }
    const href = node.getAttribute("href");
    if (!href) {
      return;
    }
    node.setAttribute("rel", "noreferrer noopener");
    node.setAttribute("target", "_blank");
  });
}

export function toSanitizedMarkdownHtml(markdown: string): string {
  const input = markdown.trim();
  if (!input) {
    return "";
  }
  installHooks();
  if (input.length <= MARKDOWN_CACHE_MAX_CHARS) {
    const cached = getCachedMarkdown(input);
    if (cached !== null) {
      return cached;
    }
  }
  const truncated = truncateText(input, MARKDOWN_CHAR_LIMIT);
  const suffix = truncated.truncated
    ? `\n\n… truncated (${truncated.total} chars, showing first ${truncated.text.length}).`
    : "";
  if (truncated.text.length > MARKDOWN_PARSE_LIMIT) {
    const escaped = escapeHtml(`${truncated.text}${suffix}`);
    const html = `<pre class="code-block">${escaped}</pre>`;
    const sanitized = DOMPurify.sanitize(html, {
      ALLOWED_TAGS: allowedTags,
      ALLOWED_ATTR: allowedAttrs,
    });
    if (input.length <= MARKDOWN_CACHE_MAX_CHARS) {
      setCachedMarkdown(input, sanitized);
    }
    return sanitized;
  }
  // UX-R12 / JAISIU-3336: pre-process so malformed fences and indented
  // blocks are rendered as syntax-highlighted code blocks too. The actual
  // language auto-detection happens via `highlightAuto` inside the
  // `code` renderer callback above.
  const preprocessed = preprocessMarkdown(`${truncated.text}${suffix}`);
  const rendered = marked.parse(preprocessed) as string;
  const sanitized = DOMPurify.sanitize(rendered, {
    ALLOWED_TAGS: allowedTags,
    ALLOWED_ATTR: allowedAttrs,
  });
  if (input.length <= MARKDOWN_CACHE_MAX_CHARS) {
    setCachedMarkdown(input, sanitized);
  }
  return sanitized;
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}
